import { useState } from 'react';
import { ScrollReveal } from './ScrollReveal';
import { useLang } from '@/lib/translations';
import { ArrowRight, Zap, Layers, Check } from 'lucide-react';

const ARCH_LAYERS = ['Interface','Orchestration','Agents','Memory','Integration','Policy','Approval','Audit','Executive'];

const SETUP_TIERS = [
  { name: 'Architecture Discovery', price: 'From \u20ac15,000', desc: 'Strategic mapping and multi-layer blueprint.',
    bestFor: 'Organizations exploring how a private multi-layer workforce could be structured.',
    features: ['Executive discovery session','Operating model review','Workflow & department mapping','Initial layer design & blueprint','Architecture fit assessment','Deployment opportunity analysis'],
    layers: [1,1,0,0,0,0,0,0,0], highlight: false, badge: null },
  { name: 'Core Layer Deployment', price: 'From \u20ac35,000', desc: 'Foundational orchestration and specialist agents.',
    bestFor: 'Businesses ready to launch foundational orchestration and specialist agent layers.',
    features: ['Interface & channel layer','Orchestration layer','Specialist agent layer','Foundational memory','Selected workflow setup','Essential permissions','Training & onboarding'],
    layers: [1,1,1,1,0,0,0,0,0], highlight: false, badge: null },
  { name: 'Multi-Layer Business', price: 'From \u20ac75,000+', desc: 'Full business-grade architecture with governance.',
    bestFor: 'Organizations needing broader coverage, memory, approvals, and executive visibility.',
    features: ['All core layers included','Deep memory & context','Business integrations','Policy & permission design','Approval framework','Executive visibility layer','Broader workflow architecture'],
    layers: [1,1,1,1,1,1,1,0,1], highlight: true, badge: 'Most Selected' },
  { name: 'Enterprise Control', price: 'Custom', desc: 'Deep governance, auditability, and multi-team control.',
    bestFor: 'Larger organizations with governance, integration, and multi-team complexity.',
    features: ['All nine architectural layers','Advanced orchestration logic','Custom integration design','Approval choreography','Full audit & traceability','Executive command environment','Premium implementation'],
    layers: [1,1,1,1,1,1,1,1,1], highlight: false, badge: 'Full Stack' },
  { name: 'Sovereign / White-Glove', price: 'Custom Strategic', desc: 'Bespoke private architecture for elite environments.',
    bestFor: 'Elite environments requiring bespoke private deployment and strategic oversight.',
    features: ['Bespoke private architecture','Multi-entity deployment','Advanced governance design','Strategic rollout planning','Elevated executive layer','White-glove implementation','Dedicated account director'],
    layers: [1,1,1,1,1,1,1,1,1], highlight: false, badge: 'White-Glove' },
];

const MONTHLY_TIERS = [
  { name: 'Agent Essentials', price: '\u20ac2,500', period: '/month', desc: 'Core operations and maintenance.',
    bestFor: 'Teams starting with a focused agent deployment.',
    features: ['Up to 3 active agents','Standard orchestration','Basic memory','Email support (48h)','Monthly report'],
    layers: [1,1,1,1,0,0,0,0,0], highlight: false, badge: null },
  { name: 'Agent Professional', price: '\u20ac5,500', period: '/month', desc: 'Full coverage with priority support.',
    bestFor: 'Growing organizations with multi-department needs.',
    features: ['Up to 7 active agents','Advanced orchestration','Full memory','Priority support (12h)','Weekly insights','Quarterly strategy'],
    layers: [1,1,1,1,1,1,0,0,0], highlight: false, badge: null },
  { name: 'Agent Enterprise', price: '\u20ac12,000', period: '/month', desc: 'Premium multi-layer operations.',
    bestFor: 'Organizations operating across all architectural layers.',
    features: ['Unlimited agents','Enterprise orchestration','Advanced memory','Dedicated support (4h)','Real-time dashboards','Monthly strategy','Custom integrations'],
    layers: [1,1,1,1,1,1,1,1,1], highlight: true, badge: 'Most Selected' },
  { name: 'Agent Sovereign', price: 'Custom', period: '/month', desc: 'Dedicated infrastructure, white-glove.',
    bestFor: 'Elite environments with sovereign data and strategic requirements.',
    features: ['Dedicated infrastructure','Sovereign data control','Multi-entity management','24/7 premium operations','Custom SLA','Executive quarterly reviews'],
    layers: [1,1,1,1,1,1,1,1,1], highlight: false, badge: 'White-Glove' },
];

const PRICING_DRIVERS = [
  'Architectural depth','Active layers','Departments covered','Workflow complexity','Integration scope',
  'Memory design','Permission design','Approval routing','Executive visibility','Multi-entity scope',
];

const scrollToContact = (e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({behavior:'smooth'}); };

function LayerBar({ layers }) {
  const count = layers.filter(Boolean).length;
  return (
    <div className="mb-5">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[9px] font-mono text-aurocle-text-secondary uppercase tracking-wider">Architecture Layers</span>
        <span className="text-[9px] font-mono text-aurocle-gold/60">{count}/9</span>
      </div>
      <div className="flex gap-[3px]">
        {ARCH_LAYERS.map((layer, j) => (
          <div key={j} className="group relative flex-1">
            <div className={`h-2 rounded-sm transition-all ${layers[j] ? 'bg-aurocle-gold/70 shadow-[0_0_6px_rgba(212,175,55,0.3)]' : 'bg-white/6'}`} />
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-aurocle-elevated border border-aurocle-gold/15 text-[8px] font-mono text-aurocle-text whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 shadow-lg">
              {layer}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TierCard({ tier, p, testId }) {
  return (
    <div className={`h-full flex flex-col relative overflow-hidden ${tier.highlight ? 'glass-card border-aurocle-gold/25 shadow-[0_0_30px_rgba(212,175,55,0.06)]' : 'premium-card'}`} data-testid={testId}>
      {tier.highlight && <div className="absolute top-0 right-0 w-40 h-40 bg-aurocle-gold/8 rounded-full blur-[80px] pointer-events-none" />}

      <div className="relative z-10 p-6 flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="text-[10px] font-mono text-aurocle-gold/60 uppercase tracking-wider">{tier.name}</div>
          {tier.badge && <span className="pill pill-gold text-[8px] shrink-0 ml-2">{tier.badge}</span>}
        </div>

        <div className="flex items-baseline gap-1.5 mb-1">
          <span className="font-heading text-2xl font-medium text-aurocle-text">{tier.price}</span>
          {tier.period && <span className="text-xs text-aurocle-text-secondary font-mono">{tier.period}</span>}
        </div>
        <p className="text-xs text-aurocle-platinum-muted mb-5">{tier.desc}</p>

        <LayerBar layers={tier.layers} />

        {/* Features */}
        <div className="space-y-2 flex-1">
          {tier.features.map((f, j) => (
            <div key={j} className="flex items-start gap-2">
              <Check size={10} className="text-aurocle-gold/50 mt-0.5 shrink-0" />
              <span className="text-xs text-aurocle-text-secondary leading-relaxed">{f}</span>
            </div>
          ))}
        </div>

        {/* Best for */}
        {tier.bestFor && (
          <div className="mt-4 pt-4 border-t border-white/5">
            <div className="text-[8px] font-mono text-aurocle-gold/40 uppercase tracking-wider mb-1">Best for</div>
            <p className="text-[10px] text-aurocle-platinum-muted leading-relaxed">{tier.bestFor}</p>
          </div>
        )}

        {/* CTA */}
        <a href="#contact" onClick={scrollToContact}
          className={`mt-5 text-xs flex items-center gap-1.5 cursor-pointer ${tier.highlight ? 'text-aurocle-gold font-medium' : 'text-aurocle-text-secondary hover:text-aurocle-gold'} transition-colors`}>
          {p.discuss} <ArrowRight size={10} />
        </a>
      </div>
    </div>
  );
}

export default function Pricing() {
  const { t } = useLang();
  const p = t.pricing;
  const [tab, setTab] = useState('setup');
  const tiers = tab === 'setup' ? SETUP_TIERS : MONTHLY_TIERS;

  return (
    <section id="pricing" className="relative py-24 sm:py-32 overflow-hidden" data-testid="pricing-section">
      <div className="absolute inset-0 grid-bg pointer-events-none opacity-20" />
      <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-aurocle-gold/3 rounded-full blur-[200px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <ScrollReveal>
          <div className="text-center mb-12">
            <div className="overline mb-4">{p.overline}</div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-aurocle-text mb-4">{p.title}</h2>
            <p className="text-base text-aurocle-platinum-muted max-w-2xl mx-auto">{p.sub}</p>
          </div>
        </ScrollReveal>

        {/* Tabs */}
        <ScrollReveal delay={50}>
          <div className="flex justify-center gap-3 mb-12">
            <button onClick={() => setTab('setup')} data-testid="pricing-tab-setup"
              className={`px-6 py-3 text-xs font-mono uppercase tracking-wider transition-all border flex items-center gap-2 ${
                tab === 'setup' ? 'bg-aurocle-gold/10 border-aurocle-gold/30 text-aurocle-gold' : 'border-white/8 text-aurocle-text-secondary hover:text-aurocle-text'}`}>
              <Layers size={12} /> {p.setupTab}
            </button>
            <button onClick={() => setTab('monthly')} data-testid="pricing-tab-monthly"
              className={`px-6 py-3 text-xs font-mono uppercase tracking-wider transition-all border flex items-center gap-2 ${
                tab === 'monthly' ? 'bg-aurocle-gold/10 border-aurocle-gold/30 text-aurocle-gold' : 'border-white/8 text-aurocle-text-secondary hover:text-aurocle-text'}`}>
              <Zap size={12} /> {p.monthlyTitle}
            </button>
          </div>
        </ScrollReveal>

        {/* Setup: 3-2 layout */}
        {tab === 'setup' && (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-5">
              {tiers.slice(0, 3).map((tier, i) => (
                <ScrollReveal key={i} delay={i * 80}>
                  <TierCard tier={tier} p={p} testId={`pricing-tier-setup-${i}`} />
                </ScrollReveal>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
              {tiers.slice(3).map((tier, i) => (
                <ScrollReveal key={i + 3} delay={(i + 3) * 80}>
                  <TierCard tier={tier} p={p} testId={`pricing-tier-setup-${i + 3}`} />
                </ScrollReveal>
              ))}
            </div>
          </>
        )}

        {/* Monthly: 4 columns */}
        {tab === 'monthly' && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {tiers.map((tier, i) => (
              <ScrollReveal key={i} delay={i * 80}>
                <TierCard tier={tier} p={p} testId={`pricing-tier-monthly-${i}`} />
              </ScrollReveal>
            ))}
          </div>
        )}

        {/* Why architecture-based */}
        <ScrollReveal delay={300}>
          <div className="mt-16 glass-card border-aurocle-gold/10 p-8 lg:p-10 max-w-4xl mx-auto relative overflow-hidden flash-sweep">
            <div className="relative grid lg:grid-cols-2 gap-8">
              <div>
                <div className="text-xs font-mono text-aurocle-gold/60 uppercase tracking-wider mb-3">{p.whyTitle}</div>
                <p className="text-sm text-aurocle-text leading-relaxed mb-4">{p.whyBody}</p>
                <p className="text-xs text-aurocle-platinum-muted leading-relaxed">The number of active layers, workflows, departments, controls, integrations, and decision pathways materially changes both the value and implementation depth of the system.</p>
              </div>
              <div>
                <div className="text-[10px] font-mono text-aurocle-text-secondary uppercase tracking-wider mb-3">What Drives Pricing</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {PRICING_DRIVERS.map((d, i) => (
                    <div key={i} className="flex items-center gap-2 text-[10px] text-aurocle-text px-2.5 py-2 border border-white/4 hover:border-aurocle-gold/15 transition-colors">
                      <div className="w-1 h-1 rounded-full bg-aurocle-gold/50 shrink-0" />{d}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={400}>
          <div className="text-center mt-10 text-xs text-aurocle-platinum-muted max-w-2xl mx-auto">{p.footer}</div>
        </ScrollReveal>
      </div>
    </section>
  );
}
